import React, { useState } from 'react';
import { MR_EXCEL_SHORTCUTS } from '../data/shortcutsLibrary';
import { MR_EXCEL_SHORTCUT_CATEGORY_META } from '../data/shortcutsCategoryMeta';

// Helper to tokenise key strings
const tokeniseKeys = (value) => {
  if (!value) return [];
  let text = String(value).trim();
  text = text.replace(/(Ctrl|Shift|Alt|Fn)(?=[^\s])/g, '$1 ');
  text = text.replace(/(⌘|⇧|⌥|⌃|←|→|↑|↓)/g, ' $1 ');
  text = text.replace(/\s+/g, ' ').trim();
  return text.split(' ').filter(Boolean);
};

// Keyboard token renderer component
function KeyRenderer({ value, platform }) {
  const tokens = tokeniseKeys(value);
  if (!tokens.length) return <span className="shortcut-na">—</span>;
  return (
    <>
      {tokens.map((token, idx) => (
        <kbd key={idx} className={`shortcut-key ${platform}-key`}>
          {token}
        </kbd>
      ))}
    </>
  );
}

export default function Shortcuts({ navigate }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [mostUsedOnly, setMostUsedOnly] = useState(false);

  const categoryOrder = [...new Set(MR_EXCEL_SHORTCUTS.map((item) => item.category))];

  const categoryId = (category) => {
    const meta = MR_EXCEL_SHORTCUT_CATEGORY_META[category] || {};
    return meta.id || category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const handleClear = () => {
    setSearchQuery('');
    setMostUsedOnly(false);
  };

  const handleCategoryJump = (categoryName) => (e) => {
    e.preventDefault();
    const id = categoryId(categoryName);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Filter shortcuts
  const term = searchQuery.trim().toLowerCase();
  let shownCount = 0;

  const groupedData = categoryOrder.map((category) => {
    const rows = MR_EXCEL_SHORTCUTS.filter((item) => {
      if (item.category !== category) return false;
      if (mostUsedOnly && String(item.level).toLowerCase() !== 'most used') return false;
      if (!term) return true;
      return `${item.title} ${item.windows} ${item.mac} ${item.category} ${item.level}`
        .toLowerCase()
        .includes(term);
    });

    shownCount += rows.length;
    return { category, rows };
  }).filter((group) => group.rows.length > 0);

  return (
    <main id="shortcut-app">
      <section className="page-hero">
        <div className="container">
          <span className="eyebrow">Free Excel shortcut library</span>
          <h1>Keyboard shortcuts for Excel</h1>
          <p className="lead max-820">
            Browse task-based shortcuts to work faster and reduce mouse fatigue in Excel. Select a shortcut to see its step-by-step description and practical example.
          </p>
        </div>
      </section>

      <section className="shortcut-tools-section">
        <div className="container shortcut-tools-grid">
          <div className="shortcut-action-area">
            <button
              className="shortcut-action-primary"
              id="print-shortcuts"
              type="button"
              onClick={() => window.print()}
            >
              Print / Save PDF
            </button>
            <button
              className={`shortcut-action-secondary ${mostUsedOnly ? 'active' : ''}`}
              id="show-most-used"
              type="button"
              aria-pressed={mostUsedOnly}
              onClick={() => setMostUsedOnly(!mostUsedOnly)}
            >
              {mostUsedOnly ? 'Show All Shortcuts' : 'Show Most Used'}
            </button>
          </div>
          <nav className="shortcut-category-nav" aria-label="Shortcut categories">
            <h2>Jump to Category</h2>
            <div id="category-links" className="shortcut-category-links">
              {categoryOrder.map((category) => (
                <a
                  key={category}
                  className="shortcut-category-link"
                  href={`#${categoryId(category)}`}
                  onClick={handleCategoryJump(category)}
                >
                  {category}
                </a>
              ))}
            </div>
          </nav>
        </div>
      </section>

      <section className="shortcut-search-section">
        <div className="container">
          <label className="shortcut-search-box" htmlFor="shortcut-search">
            <span className="shortcut-search-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="27" height="27" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="11" cy="11" r="7"></circle>
                <path d="m20 20-4-4"></path>
              </svg>
            </span>
            <input
              id="shortcut-search"
              type="search"
              placeholder="Search Shortcuts..."
              autoComplete="off"
              aria-label="Filter Excel shortcuts"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </label>
          <div className="shortcut-filter-note">
            Showing <strong>{shownCount}</strong> shortcuts
            {(searchQuery || mostUsedOnly) && (
              <button type="button" id="clear-shortcut-filter" onClick={handleClear}>
                Clear filter
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="shortcut-list-section">
        <div className="container">
          <div id="shortcut-results">
            {groupedData.map(({ category, rows }) => (
              <section key={category} className="shortcut-section" id={categoryId(category)}>
                <h2 className="shortcut-section-title">{category}</h2>
                <div className="shortcut-table" role="table" aria-label={`${category} Excel shortcuts`}>
                  <div className="shortcut-table-head" role="row">
                    <div role="columnheader">Shortcut</div>
                    <div role="columnheader">Windows</div>
                    <div role="columnheader">Mac</div>
                  </div>
                  {rows.map((item) => (
                    <a
                      key={item.slug}
                      className="shortcut-row"
                      role="row"
                      href={`#/shortcuts/${item.slug}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`shortcuts/${item.slug}`);
                        window.scrollTo({ top: 0 });
                      }}
                    >
                      <div className="shortcut-row-title" role="cell">
                        <span>{item.title}</span>
                      </div>
                      <div className="shortcut-keys shortcut-win" role="cell">
                        <KeyRenderer value={item.windows} platform="windows" />
                      </div>
                      <div className="shortcut-keys shortcut-mac" role="cell">
                        <KeyRenderer value={item.mac} platform="mac" />
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {shownCount === 0 && (
            <div className="shortcut-empty" id="shortcut-empty">
              <h2>No shortcuts found</h2>
              <p>Try a different keyword, shortcut key or category.</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
